/**
 * Bucket bank accounts into jurisdictions for the By Jurisdiction cards.
 *
 * Membership comes STRICTLY from the backend predicate: each account carries a
 * `jurisdiction` field computed by services/bank_balance_positions.py
 * account_jurisdiction (ownership-first) and served on /bank-balances/summary.
 * The cards group by that field alone. There is deliberately no entity-store
 * fallback: re-deriving buckets from the frontend store is exactly what drifted
 * from the predicate whenever an account's entity_id failed to resolve there
 * (the account dropped to Unassigned on the card while the predicate bucketed it
 * correctly). A fallback is where that drift respawns, so there isn't one.
 *
 * An account lands in Unassigned only when the predicate marks it "unassigned"
 * (or any value not mapped in PREDICATE_BUCKET_TO_CONFIG); nothing else does. So
 * the sum of all jurisdiction buckets plus Unassigned equals the group total,
 * bucket-for-bucket with the predicate breakdown.
 *
 * balByEntity is a parallel structure used only for the per-entity drilldown's
 * DISPLAY (names and sub-line balances) inside a card; it resolves against the
 * store but does NOT influence which bucket an account counts toward.
 */

import { PREDICATE_BUCKET_TO_CONFIG } from './jurisdictionConfig';

export function bucketAccountsByJurisdiction(accounts, entityById, entityByType) {
  var jurs = {};
  var ents = {};
  var unassigned = { balances: {}, accounts: [] };

  for (var i = 0; i < (accounts || []).length; i++) {
    var account = accounts[i];
    var cur = account.currency || 'GBP';
    // null/undefined balance means "feed never reported", not zero money --
    // the account still counts toward its bucket's accounts list (and any
    // no-balance-reported display), it just contributes nothing to the sum.
    var bal = account.balance;
    var hasBal = bal != null;

    // Bucket membership: predicate only.
    var configKey = PREDICATE_BUCKET_TO_CONFIG[account.jurisdiction];
    if (configKey) {
      if (!jurs[configKey]) jurs[configKey] = { balances: {}, accounts: [] };
      if (hasBal) jurs[configKey].balances[cur] = (jurs[configKey].balances[cur] || 0) + bal;
      jurs[configKey].accounts.push(account);
    } else {
      // Predicate-unassigned (or unknown bucket): counted here, never dropped.
      if (hasBal) unassigned.balances[cur] = (unassigned.balances[cur] || 0) + bal;
      unassigned.accounts.push(account);
    }

    // Per-entity drilldown DISPLAY only (store-resolved names/balances). Does not
    // affect the bucket above.
    var entity = entityById[account.entity_id] || entityByType[account.entity_id];
    if (entity) {
      if (!ents[entity.id]) ents[entity.id] = { entity: entity, balances: {}, accounts: [] };
      if (hasBal) ents[entity.id].balances[cur] = (ents[entity.id].balances[cur] || 0) + bal;
      ents[entity.id].accounts.push(account);
    }
  }

  return { balByJurisdiction: jurs, balByEntity: ents, unassigned: unassigned };
}
